import * as React from 'react';
import Collapse from 'antd/lib/collapse';
import Space from 'antd/lib/space';
import Text from 'antd/lib/typography/Text';
import { ArrowRightOutlined, DownOutlined, MobileOutlined } from '@ant-design/icons';

import {
    antGrey,
    buildContainerStyle,
    releaseStepDetailsContainerModuleStyle,
    runwayColors,
} from '../styles/globalStyles';

import BetaSoakModule from '../betaModules/BetaSoakModule';
import BetaWhatToTestModule from '../betaModules/BetaWhatToTestModule';
import BetaGroupsModule from '../betaModules/BetaGroupsModule';

import { isGoogleBetaIntegration } from '@/helpers';
import {
    AppleAppStoreState,
    BetaBuild,
    BetaStatus,
    BetaTester,
    BetaTestingGroup,
    BetaTestingStyle,
    BuildDistroBuild,
    BuildInfo,
    GooglePlayStoreState,
    IntegrationCapability,
    IntegrationRunwayBuildDistroBeta,
    IntegrationType,
    ReleaseStatus,
    ReleaseStepType,
    TestTrack,
} from '@/types';
import { UpdateBetaStyleRequest } from '@/actions/appSettings';
import { AppBuildComponent, CIBuildComponent } from '../BuildComponents';
import LinkageIcon from '../icons/LinkageIcon';
import { getLocaleDateTimeFormat } from '@/timeHelpers';
import { dayjs } from '@/extendedDayjs';
import { useSelector } from 'react-redux';
import {
    selectAppSettings,
    selectIntegrationWithCapabilityFromInstallations,
    selectReleaseStatus,
    selectAppTimeFormatSetting,
} from '@/selectors/selectors';
import { RootState } from '@/reducers/reducers';

import { FeatureSection, Nullable, ReleaseComponentProps, StepRunComponentProps } from '@/typescriptHelpers';
import Tooltip from 'antd/lib/tooltip';
import StabilityMetricsContainer from '../selectedAppStoreBuild/StabilityMetricsContainer';
import ActiveBetaBuildHeader from './ActiveBetaBuildHeader';
import { EmptyStateWrapper } from '../releaseEmptyState/EmptyStateWrapper';
import {
    BuildBucketAvatar,
    BuildNumberTag,
    BuildTypeAndDate,
    CommitDetailsPopover,
} from '../buildDistribution/CommonComponents';
import BuildDistroInstallMenu from '../buildDistribution/BuildDistroInstallMenu';
import { statusColorForBuildStatus } from '@/helpers/displayHelpers';
import Badge from 'antd/lib/badge';
import Button from 'antd/lib/button';
import { Link } from 'react-router-dom';
import { createAppBuildDistroBucketRoute } from '@/helpers/routeHelpers';

interface BetaTestingActiveBuildProps extends ReleaseComponentProps {
    appStoreState?: AppleAppStoreState | GooglePlayStoreState;
    betaIntegrationType?: IntegrationType;
    betaStyle: BetaTestingStyle;
    soakDurationInSeconds: number;
    soakFinished: boolean;
    showSoakTimeRemaining: boolean;
    soakStartedAt: string;
    showElapsedTime: boolean;
    betaStyleIsUpdating: boolean;
    saveBetaStyle: (input: UpdateBetaStyleRequest) => void;
    betaStyleUpdated: boolean;
    activeBetaBuilds: BetaBuild[];
    testers: BetaTester[];
    betaTestingActiveBuildIdentifier?: string;
    defaultTestTrack?: string;
    consoleUrl?: string;
    testTracks?: TestTrack[];
    groups: BetaTestingGroup[];
    activeCIBuild?: BuildInfo;
    releaseStepType: ReleaseStepType;
    onViewBuildDetails?: (build: BuildDistroBuild) => void;
    stepRunComponentProps: Nullable<StepRunComponentProps>;
    integrationInstallationIds: string[];
    autoApplyBetaBuildNotesAutomationIsActive: boolean;
    autoAssignBetaGroupsEnabled: boolean;
}

const BetaTestingActiveBuild = (props: BetaTestingActiveBuildProps): JSX.Element => {
    const hasActiveBetaBuilds = props.activeBetaBuilds.length > 0;
    const betaBuild = hasActiveBetaBuilds ? props.activeBetaBuilds[0] : null;
    const betaIntegrationType = betaBuild?.appStoreBuild.appStoreBuild.integrationId ?? props.betaIntegrationType;
    const hasBetaIntegration = !!props.betaIntegrationType;

    const appImageUrl = useSelector((state: RootState) => selectAppSettings(state, props.appId).appImageUrl);

    const releaseStatus = useSelector((state: RootState) => selectReleaseStatus(state, props.releaseId));

    const releaseIsActive = releaseStatus === ReleaseStatus.Active;

    const activeBuildsWithStabInfo = props.activeBetaBuilds
        .filter((build) => !!build.appStoreBuild.stabilityInfo)
        .map((build) => build.appStoreBuild);

    const disableGoogleMetadataEditing =
        isGoogleBetaIntegration(betaIntegrationType) && betaBuild && betaBuild.betaStatus === BetaStatus.NotTesting;

    const selectedBucket = useSelector((state: RootState) => {
        const betaIntegration = selectIntegrationWithCapabilityFromInstallations(
            state,
            props.appId,
            IntegrationCapability.BetaTesting,
            props.integrationInstallationIds,
        ) as IntegrationRunwayBuildDistroBeta;

        return betaIntegration?.data?.selectedBucket;
    });

    const use24HourTimeFormat = useSelector((state: RootState) => selectAppTimeFormatSetting(state, props.appId));

    const buildDistroBuild = betaBuild?.buildDistroBuild;

    const isBuildDistroBuild = !!selectedBucket && !!buildDistroBuild;

    const buildHeader = () => {
        if (isBuildDistroBuild) {
            return (
                <div
                    style={{
                        display: 'flex',
                        width: '100%',
                        flexDirection: 'column',
                        gap: 12,
                    }}
                >
                    <Space size={16} align="center">
                        <BuildTypeAndDate
                            uploadedAt={buildDistroBuild.uploadedAt}
                            ciBuild={buildDistroBuild.ciBuild}
                            appId={props.appId}
                        />
                        <Space size={16} align="center">
                            {buildDistroBuild?.ciBuild && (
                                <Badge dot color={statusColorForBuildStatus(buildDistroBuild?.ciBuild.buildStatus)} />
                            )}
                            <BuildNumberTag build={buildDistroBuild} />
                        </Space>
                        {buildDistroBuild?.ciBuild && <CommitDetailsPopover ciBuild={buildDistroBuild.ciBuild} />}
                    </Space>
                </div>
            );
        }

        if (hasActiveBetaBuilds) {
            return (
                <Space align="center">
                    {props.activeBetaBuilds.map((activeBetaBuild, index) => {
                        const uploadedOn = dayjs(activeBetaBuild.appStoreBuild.appStoreBuild.uploadedAt).format(
                            getLocaleDateTimeFormat('mobile', use24HourTimeFormat),
                        );

                        return (
                            <Space style={buildContainerStyle} align="center" key={index}>
                                {activeBetaBuild.appStoreBuild && (
                                    <Tooltip title={`Uploaded on ${uploadedOn}`}>
                                        <div>
                                            <AppBuildComponent
                                                integrationId={
                                                    activeBetaBuild.appStoreBuild.appStoreBuild.integrationId
                                                }
                                                buildIdentifier={
                                                    activeBetaBuild.appStoreBuild.appStoreBuild.buildNumber
                                                }
                                                isProcessed={activeBetaBuild.appStoreBuild.appStoreBuild.isProcessed}
                                                strong
                                            />
                                        </div>
                                    </Tooltip>
                                )}
                                {activeBetaBuild.appStoreBuild && activeBetaBuild.appStoreBuild.associatedCIBuild && (
                                    <LinkageIcon width={16} height={16} />
                                )}

                                {activeBetaBuild.appStoreBuild.associatedCIBuild && (
                                    <CIBuildComponent
                                        integrationId={activeBetaBuild.appStoreBuild.associatedCIBuild.integrationId}
                                        buildIdentifier={
                                            activeBetaBuild.appStoreBuild.associatedCIBuild.buildIdentifier
                                        }
                                        strong
                                    />
                                )}
                            </Space>
                        );
                    })}
                </Space>
            );
        } else {
            return (
                <Space>
                    <div
                        style={{
                            backgroundColor: antGrey.grey3,
                            borderRadius: 8,
                            paddingLeft: 12,
                            paddingRight: 12,
                            paddingTop: 6,
                            paddingBottom: 6,
                        }}
                    >
                        {props.activeCIBuild ?
                            <CIBuildComponent
                                integrationId={props.activeCIBuild.integrationId}
                                buildIdentifier={props.activeCIBuild.buildIdentifier}
                                strong
                            />
                        :   <Text>No active build</Text>}
                    </div>
                    {props.activeCIBuild?.buildFinishedAt && (
                        <Text type="secondary">
                            {dayjs(props.activeCIBuild.buildFinishedAt).format(
                                getLocaleDateTimeFormat('abbreviatedYear', use24HourTimeFormat),
                            )}
                        </Text>
                    )}
                </Space>
            );
        }
    };

    return (
        <div style={{ width: '100%' }}>
            <Text style={{ fontSize: 20, color: antGrey.grey7, marginBottom: 24, display: 'block' }}>Active build</Text>
            <div
                style={{
                    ...releaseStepDetailsContainerModuleStyle,
                }}
            >
                {!isBuildDistroBuild && (hasBetaIntegration || hasActiveBetaBuilds) && (
                    <ActiveBetaBuildHeader
                        stepRunComponentProps={props.stepRunComponentProps}
                        orgId={props.orgId}
                        appId={props.appId}
                        releaseId={props.releaseId}
                        activeBetaBuilds={props.activeBetaBuilds}
                        appBetaIntegrationType={props.betaIntegrationType}
                        releaseIsActive={releaseIsActive}
                        betaLinkUrl={props.consoleUrl}
                        defaultTestTrack={props.defaultTestTrack}
                        betaTestingActiveBuildIdentifier={props.betaTestingActiveBuildIdentifier}
                        testTracks={props.testTracks}
                    />
                )}
                {isBuildDistroBuild && (
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: 12,
                        }}
                    >
                        <Space>
                            <BuildBucketAvatar size={24} appImageUrl={appImageUrl} bucket={selectedBucket} />
                            <Link to={createAppBuildDistroBucketRoute(props.orgId, props.appId, selectedBucket.id)}>
                                <Text
                                    style={{
                                        fontWeight: 600,
                                        fontSize: 14,
                                        color: runwayColors.primary,
                                    }}
                                >
                                    {selectedBucket.name}
                                </Text>
                            </Link>
                        </Space>
                        {buildDistroBuild && (
                            <BuildDistroInstallMenu
                                orgId={props.orgId}
                                appId={props.appId}
                                buildDistroBuild={buildDistroBuild}
                                shouldShowInfoIcon={true}
                            >
                                {(isDisabled, isLoading) => (
                                    <Button
                                        block
                                        icon={<MobileOutlined />}
                                        type="primary"
                                        loading={isLoading}
                                        disabled={isDisabled}
                                    >
                                        <Space style={{ marginLeft: 8 }}>
                                            Install
                                            <DownOutlined />
                                        </Space>
                                    </Button>
                                )}
                            </BuildDistroInstallMenu>
                        )}
                    </div>
                )}
                <Collapse
                    ghost
                    style={{ marginBottom: '32' }}
                    defaultActiveKey={['active-build']}
                    className="adjust-collapse-icon"
                >
                    <Collapse.Panel
                        key="active-build"
                        className="no-padding-collapse-panel"
                        collapsible="header"
                        header={
                            <EmptyStateWrapper
                                releaseStepType={props.releaseStepType}
                                appId={props.appId}
                                releaseId={props.releaseId}
                                featureSections={[FeatureSection.BetaTestingActiveBuild]}
                            >
                                <div>{buildHeader()}</div>
                            </EmptyStateWrapper>
                        }
                        extra={
                            isBuildDistroBuild && (
                                <Button
                                    icon={<ArrowRightOutlined />}
                                    type="link"
                                    onClick={() => {
                                        props.onViewBuildDetails?.(buildDistroBuild);
                                    }}
                                    style={{ padding: 0 }}
                                >
                                    View build details
                                </Button>
                            )
                        }
                    >
                        <Space direction="vertical" style={{ width: '100%' }} size={20}>
                            <EmptyStateWrapper
                                releaseStepType={props.releaseStepType}
                                appId={props.appId}
                                releaseId={props.releaseId}
                                featureSections={[FeatureSection.BetaTestingBetaSoak]}
                            >
                                <div>
                                    <BetaSoakModule
                                        orgId={props.orgId}
                                        releaseId={props.releaseId}
                                        betaStyle={props.betaStyle}
                                        soakDurationInSeconds={props.soakDurationInSeconds}
                                        soakFinished={props.soakFinished}
                                        showSoakTimeRemaining={props.showSoakTimeRemaining}
                                        soakStartedAt={dayjs(props.soakStartedAt)}
                                        showElapsedTime={props.showElapsedTime}
                                        appId={props.appId}
                                        betaStyleIsUpdating={props.betaStyleIsUpdating}
                                        saveBetaStyle={props.saveBetaStyle}
                                        betaStyleUpdated={props.betaStyleUpdated}
                                    />
                                </div>
                            </EmptyStateWrapper>
                            <EmptyStateWrapper
                                releaseStepType={props.releaseStepType}
                                appId={props.appId}
                                releaseId={props.releaseId}
                                featureSections={[FeatureSection.BetaTestingWhatToTest]}
                            >
                                {(isInEmptyState) => {
                                    return (
                                        (isInEmptyState || betaBuild) && (
                                            <div>
                                                <BetaWhatToTestModule
                                                    autoApplyBetaBuildNotesAutomationIsActive={
                                                        props.autoApplyBetaBuildNotesAutomationIsActive
                                                    }
                                                    stepRunComponentProps={props.stepRunComponentProps}
                                                    orgId={props.orgId}
                                                    appId={props.appId}
                                                    shouldHideEditTestInformationButton={isBuildDistroBuild}
                                                    betaIntegrationType={betaIntegrationType}
                                                    releaseId={props.releaseId}
                                                    externalBuildId={betaBuild?.appStoreBuild.appStoreBuild.id}
                                                    betaNotes={betaBuild?.betaTestingNotes ?? []}
                                                    editTestInformationDisabled={disableGoogleMetadataEditing}
                                                />
                                            </div>
                                        )
                                    );
                                }}
                            </EmptyStateWrapper>
                            <EmptyStateWrapper
                                releaseStepType={props.releaseStepType}
                                appId={props.appId}
                                releaseId={props.releaseId}
                                featureSections={[FeatureSection.BetaTestingBetaGroups]}
                            >
                                {(isInEmptyState) => {
                                    return (
                                        (isInEmptyState || betaBuild) && (
                                            <div>
                                                <BetaGroupsModule
                                                    stepRunComponentProps={props.stepRunComponentProps}
                                                    autoAssignBetaGroupsEnabled={props.autoAssignBetaGroupsEnabled}
                                                    orgId={props.orgId}
                                                    allTesters={props.testers}
                                                    allGroups={props.groups}
                                                    selectedTesters={betaBuild?.betaTesters ?? []}
                                                    selectedGroups={betaBuild?.betaGroups ?? []}
                                                    shouldHideEditGroupsButton={isBuildDistroBuild}
                                                    appId={props.appId}
                                                    releaseId={props.releaseId}
                                                    externalBuildId={betaBuild?.appStoreBuild.appStoreBuild.id}
                                                    betaIntegrationType={betaIntegrationType}
                                                    editGroupsDisabled={disableGoogleMetadataEditing}
                                                />
                                            </div>
                                        )
                                    );
                                }}
                            </EmptyStateWrapper>
                            <EmptyStateWrapper
                                releaseStepType={props.releaseStepType}
                                appId={props.appId}
                                releaseId={props.releaseId}
                                featureSections={[FeatureSection.BetaTestingStabilityModule]}
                            >
                                {(isInEmptyState) => {
                                    return (
                                        (isInEmptyState || activeBuildsWithStabInfo.length > 0) && (
                                            <div
                                                style={{
                                                    ...releaseStepDetailsContainerModuleStyle,
                                                    backgroundColor: antGrey.grey2,
                                                }}
                                            >
                                                <StabilityMetricsContainer
                                                    appId={props.appId}
                                                    backgroundColor={'white'}
                                                    isRunwayAutoPaused={false}
                                                    builds={activeBuildsWithStabInfo}
                                                />
                                            </div>
                                        )
                                    );
                                }}
                            </EmptyStateWrapper>
                        </Space>
                    </Collapse.Panel>
                </Collapse>
            </div>
        </div>
    );
};

export default BetaTestingActiveBuild;
